/* eslint-disable react-hooks/set-state-in-effect */
/**
 * ChannelSettingsModal Component
 *
 * Kombiniertes Modal für Channel-Konfiguration:
 * - Tab 1: Anforderungen (Dimensionen, Dateigröße, Formate, Aspect Ratio)
 * - Tab 2: Optimierung (Format, Qualität, Upscaling)
 *
 * Ersetzt: ChannelRequirementsModal + ImageEditorSettingsModal
 *
 * @accessibility WCAG 2.1 Level AA compliant
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/v2/ui/Dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/v2/ui';
import { Button } from '@/components/v2/ui/Button';
import { Input } from '@/components/v2/ui/Input';
import { Label } from '@/components/v2/ui/Label';
import { Alert } from '@/components/v2/ui/Alert';
import { Settings, ShieldCheck, Sparkles, Trash2 } from 'lucide-react';
import {
  getIntegrationDefaults,
  getChannelOverrides,
} from '@/lib/api/unified-image-editor';
import { getProduct } from '@/lib/api/products';
import { queryKeys } from '@/lib/api/queryKeys';

// =============================================================================
// Types
// =============================================================================

interface ChannelRequirements {
  id?: string;
  channel_id: string;
  min_width?: number | null;
  min_height?: number | null;
  max_width?: number | null;
  max_height?: number | null;
  min_file_size_kb?: number | null;
  max_file_size_kb?: number | null;
  allowed_formats?: string[] | null;
  require_exact_aspect_ratio?: boolean;
  aspect_ratio_width?: number | null;
  aspect_ratio_height?: number | null;
}

interface ChannelSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: string;
  channelId: string;
  channelName?: string;
  initialTab?: 'requirements' | 'optimization';
}

// =============================================================================
// Constants
// =============================================================================

const FORMAT_OPTIONS = ['JPEG', 'JPG', 'PNG', 'WebP', 'AVIF', 'BMP', 'GIF'];

const FORMAT_LABELS: Record<string, string> = {
  jpeg: 'JPEG',
  png: 'PNG',
  webp: 'WebP',
  avif: 'AVIF',
};

const MODEL_LABELS: Record<string, string> = {
  espcn: 'ESPCN (Fast)',
  fsrcnn: 'FSRCNN (Balanced)',
  lapsrn: 'LapSRN (Quality)',
  edsr: 'EDSR (Best)',
};

// =============================================================================
// API Functions
// =============================================================================

async function getChannelRequirements(channelId: string): Promise<ChannelRequirements | null> {
  const response = await fetch(`/api/v1/channels/${channelId}/requirements`);
  const data = await response.json();
  if (data.success && data.requirements) {
    return data.requirements;
  }
  return null;
}

async function saveChannelRequirements(
  channelId: string,
  requirements: Partial<ChannelRequirements>
): Promise<void> {
  const response = await fetch(`/api/v1/channels/${channelId}/requirements`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(requirements),
  });

  const data = await response.json();
  if (!data.success) {
    throw new Error(data.error || 'Fehler beim Speichern');
  }
}

async function deleteChannelRequirements(channelId: string): Promise<void> {
  const response = await fetch(`/api/v1/channels/${channelId}/requirements`, {
    method: 'DELETE',
  });

  const data = await response.json();
  if (!data.success) {
    throw new Error(data.error || i18n.t('errors.deleting'));
  }
}

// =============================================================================
// Main Component
// =============================================================================

export function ChannelSettingsModal({
  isOpen,
  onClose,
  productId,
  channelId,
  channelName,
  initialTab = 'requirements',
}: ChannelSettingsModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'requirements' | 'optimization'>(initialTab);

  // =========================================================================
  // Requirements State
  // =========================================================================
  const [minWidth, setMinWidth] = useState<string>('');
  const [minHeight, setMinHeight] = useState<string>('');
  const [maxWidth, setMaxWidth] = useState<string>('');
  const [maxHeight, setMaxHeight] = useState<string>('');
  const [minFileSize, setMinFileSize] = useState<string>('');
  const [maxFileSize, setMaxFileSize] = useState<string>('');
  const [selectedFormats, setSelectedFormats] = useState<string[]>([]);
  const [requireAspectRatio, setRequireAspectRatio] = useState(false);
  const [aspectRatioWidth, setAspectRatioWidth] = useState<string>('');
  const [aspectRatioHeight, setAspectRatioHeight] = useState<string>('');

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // =========================================================================
  // Data Loading
  // =========================================================================

  // Load Requirements
  const { data: requirements, isLoading: loadingRequirements } = useQuery({
    queryKey: ['channel-requirements', channelId],
    queryFn: () => getChannelRequirements(channelId),
    enabled: isOpen && !!channelId,
  });

  // Load Product (for integration ID)
  const { data: product, isLoading: loadingProduct } = useQuery({
    queryKey: ['product', productId],
    queryFn: () => getProduct(productId),
    enabled: isOpen && !!productId,
  });

  const integrationId = product?.product?.workspace_integration_id;

  // Load Integration Defaults
  const { data: integrationDefaults, isLoading: loadingDefaults } = useQuery({
    queryKey: integrationId ? queryKeys.imageEditorSettings.integration(integrationId) : [],
    queryFn: () => (integrationId ? getIntegrationDefaults(integrationId) : null),
    enabled: isOpen && !!integrationId,
  });

  // Load Channel Overrides
  const { data: channelOverrides, isLoading: loadingOverrides } = useQuery({
    queryKey: channelId ? queryKeys.imageEditorSettings.channel(channelId) : [],
    queryFn: () => (channelId ? getChannelOverrides(channelId) : null),
    enabled: isOpen && !!channelId,
  });

  // =========================================================================
  // Populate Form
  // =========================================================================

   
  useEffect(() => {
    if (requirements) {
      setMinWidth(requirements.min_width?.toString() || '');
      setMinHeight(requirements.min_height?.toString() || '');
      setMaxWidth(requirements.max_width?.toString() || '');
      setMaxHeight(requirements.max_height?.toString() || '');
      setMinFileSize(requirements.min_file_size_kb?.toString() || '');
      setMaxFileSize(requirements.max_file_size_kb?.toString() || '');
      setSelectedFormats(requirements.allowed_formats || []);
      setRequireAspectRatio(requirements.require_exact_aspect_ratio || false);
      setAspectRatioWidth(requirements.aspect_ratio_width?.toString() || '');
      setAspectRatioHeight(requirements.aspect_ratio_height?.toString() || '');
    }
  }, [requirements]);

  // =========================================================================
  // Mutations
  // =========================================================================

  const saveMutation = useMutation({
    mutationFn: () => {
      const data: Partial<ChannelRequirements> = {
        min_width: minWidth ? parseInt(minWidth) : null,
        min_height: minHeight ? parseInt(minHeight) : null,
        max_width: maxWidth ? parseInt(maxWidth) : null,
        max_height: maxHeight ? parseInt(maxHeight) : null,
        min_file_size_kb: minFileSize ? parseInt(minFileSize) : null,
        max_file_size_kb: maxFileSize ? parseInt(maxFileSize) : null,
        allowed_formats: selectedFormats.length > 0 ? selectedFormats : null,
        require_exact_aspect_ratio: requireAspectRatio,
        aspect_ratio_width: requireAspectRatio && aspectRatioWidth ? parseInt(aspectRatioWidth) : null,
        aspect_ratio_height: requireAspectRatio && aspectRatioHeight ? parseInt(aspectRatioHeight) : null,
      };
      return saveChannelRequirements(channelId, data);
    },
    onSuccess: () => {
      setSuccess('Anforderungen erfolgreich gespeichert');
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['channel-requirements', channelId] });
    },
    onError: (err: Error) => {
      setError(err.message);
      setSuccess(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteChannelRequirements(channelId),
    onSuccess: () => {
      setSuccess(t('products.requirementsDeleted'));
      setError(null);
      clearForm();
      queryClient.invalidateQueries({ queryKey: ['channel-requirements', channelId] });
    },
    onError: (err: Error) => {
      setError(err.message);
      setSuccess(null);
    },
  });

  // =========================================================================
  // Handlers
  // =========================================================================

  const clearForm = () => {
    setMinWidth('');
    setMinHeight('');
    setMaxWidth('');
    setMaxHeight('');
    setMinFileSize('');
    setMaxFileSize('');
    setSelectedFormats([]);
    setRequireAspectRatio(false);
    setAspectRatioWidth('');
    setAspectRatioHeight('');
  };

  const handleSave = () => {
    setError(null);
    setSuccess(null);
    saveMutation.mutate();
  };

  const handleDelete = () => {
    if (window.confirm(t('products.confirmDeleteRequirements'))) {
      setError(null);
      setSuccess(null);
      deleteMutation.mutate();
    }
  };

  const toggleFormat = (format: string) => {
    setSelectedFormats((prev) =>
      prev.includes(format) ? prev.filter((f) => f !== format) : [...prev, format]
    );
  };

  if (!isOpen) return null;

  const isLoading = loadingRequirements || loadingProduct || loadingDefaults || loadingOverrides;
  const hasOverrides = channelOverrides !== null && channelOverrides !== undefined;
  const optimizationSettings = hasOverrides ? channelOverrides : integrationDefaults;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <Settings className="h-5 w-5 text-neutral-700 dark:text-neutral-300" />
            <div>
              <DialogTitle>Channel-Einstellungen</DialogTitle>
              {channelName && (
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                  {channelName}
                </p>
              )}
            </div>
          </div>
        </DialogHeader>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'requirements' | 'optimization')} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="mx-0 mb-4">
            <TabsTrigger value="requirements" className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" />
              {t('products.channelRequirements')}
            </TabsTrigger>
            <TabsTrigger value="optimization" className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              {t('products.optimization')}
            </TabsTrigger>
          </TabsList>

          {/* Requirements Tab */}
          <TabsContent value="requirements" className="flex-1 overflow-y-auto pr-2">
            {loadingRequirements ? (
              <LoadingSpinner />
            ) : (
              <div className="space-y-6">
                {/* Error/Success Messages */}
                {error && <Alert variant="destructive"><p>{error}</p></Alert>}
                {success && <Alert><p>{success}</p></Alert>}

                {/* Dimensions */}
                <Section title="Dimensionen (Pixel)">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="min-width">Min. Breite</Label>
                      <Input id="min-width" type="number" value={minWidth} onChange={(e) => setMinWidth(e.target.value)} placeholder="z.B. 1000" />
                    </div>
                    <div>
                      <Label htmlFor="max-width">Max. Breite</Label>
                      <Input id="max-width" type="number" value={maxWidth} onChange={(e) => setMaxWidth(e.target.value)} placeholder="z.B. 5000" />
                    </div>
                    <div>
                      <Label htmlFor="min-height">{t('products.minHeight')}</Label>
                      <Input id="min-height" type="number" value={minHeight} onChange={(e) => setMinHeight(e.target.value)} placeholder="z.B. 1000" />
                    </div>
                    <div>
                      <Label htmlFor="max-height">{t('products.maxHeight')}</Label>
                      <Input id="max-height" type="number" value={maxHeight} onChange={(e) => setMaxHeight(e.target.value)} placeholder="z.B. 5000" />
                    </div>
                  </div>
                </Section>

                {/* File Size */}
                <Section title={t('products.fileSizeKb')}>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="min-file-size">{t('products.minSize')}</Label>
                      <Input id="min-file-size" type="number" value={minFileSize} onChange={(e) => setMinFileSize(e.target.value)} placeholder="z.B. 100" />
                    </div>
                    <div>
                      <Label htmlFor="max-file-size">{t('products.maxSize')}</Label>
                      <Input id="max-file-size" type="number" value={maxFileSize} onChange={(e) => setMaxFileSize(e.target.value)} placeholder="z.B. 10000" />
                    </div>
                  </div>
                </Section>

                {/* Formats */}
                <Section title="Erlaubte Formate">
                  <div className="flex flex-wrap gap-2">
                    {FORMAT_OPTIONS.map((format) => (
                      <button
                        key={format}
                        type="button"
                        onClick={() => toggleFormat(format)}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                          selectedFormats.includes(format)
                            ? 'bg-primary-600 text-white'
                            : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700'
                        }`}
                      >
                        {format}
                      </button>
                    ))}
                  </div>
                </Section>

                {/* Aspect Ratio */}
                <Section title={t('gallery.aspectRatioLabel')}>
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      type="checkbox"
                      id="require-aspect-ratio"
                      checked={requireAspectRatio}
                      onChange={(e) => setRequireAspectRatio(e.target.checked)}
                      className="h-4 w-4 rounded border-neutral-300"
                    />
                    <Label htmlFor="require-aspect-ratio" className="cursor-pointer">
                      {t('products.exactAspectRatio')}
                    </Label>
                  </div>

                  {requireAspectRatio && (
                    <div className="grid grid-cols-2 gap-4 ml-6">
                      <div>
                        <Label htmlFor="aspect-width">Breite</Label>
                        <Input id="aspect-width" type="number" value={aspectRatioWidth} onChange={(e) => setAspectRatioWidth(e.target.value)} placeholder="z.B. 16" />
                      </div>
                      <div>
                        <Label htmlFor="aspect-height">{t('products.aspectHeight')}</Label>
                        <Input id="aspect-height" type="number" value={aspectRatioHeight} onChange={(e) => setAspectRatioHeight(e.target.value)} placeholder="z.B. 9" />
                      </div>
                    </div>
                  )}
                </Section>
              </div>
            )}
          </TabsContent>

          {/* Optimization Tab */}
          <TabsContent value="optimization" className="flex-1 overflow-y-auto pr-2">
            {isLoading ? (
              <LoadingSpinner />
            ) : optimizationSettings ? (
              <div className="space-y-6">
                <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-4">
                  {hasOverrides ? 'Channel-spezifische Einstellungen' : 'Integration-Standard'}
                </div>

                {/* Format */}
                <SettingRow
                  label="Bildformat"
                  value={
                    hasOverrides && channelOverrides?.override_format
                      ? FORMAT_LABELS[channelOverrides.override_format] || channelOverrides.override_format
                      : integrationDefaults?.default_format
                      ? FORMAT_LABELS[integrationDefaults.default_format] || integrationDefaults.default_format
                      : 'Nicht konfiguriert'
                  }
                  isDefault={hasOverrides && !channelOverrides?.override_format}
                />

                {/* Quality */}
                <SettingRow
                  label={t('products.quality')}
                  value={
                    hasOverrides && channelOverrides?.override_quality !== undefined
                      ? `${channelOverrides.override_quality}%`
                      : integrationDefaults?.default_quality !== undefined
                      ? `${integrationDefaults.default_quality}%`
                      : 'Nicht konfiguriert'
                  }
                  isDefault={hasOverrides && channelOverrides?.override_quality === undefined}
                />

                {/* Dimensions */}
                <div className="grid grid-cols-2 gap-4">
                  <SettingRow
                    label="Breite"
                    value={
                      hasOverrides && channelOverrides?.override_width
                        ? `${channelOverrides.override_width}px`
                        : integrationDefaults?.default_width
                        ? `${integrationDefaults.default_width}px`
                        : 'Auto'
                    }
                    isDefault={hasOverrides && !channelOverrides?.override_width && !!integrationDefaults?.default_width}
                  />
                  <SettingRow
                    label={t('products.aspectHeight')}
                    value={
                      hasOverrides && channelOverrides?.override_height
                        ? `${channelOverrides.override_height}px`
                        : integrationDefaults?.default_height
                        ? `${integrationDefaults.default_height}px`
                        : 'Auto'
                    }
                    isDefault={hasOverrides && !channelOverrides?.override_height && !!integrationDefaults?.default_height}
                  />
                </div>

                {/* Upscale Factor */}
                <SettingRow
                  label="Hochskalierung"
                  value={
                    hasOverrides && channelOverrides?.override_upscale_factor
                      ? `${channelOverrides.override_upscale_factor}x`
                      : integrationDefaults?.default_upscale_factor
                      ? `${integrationDefaults.default_upscale_factor}x`
                      : '1.0x'
                  }
                  isDefault={hasOverrides && !channelOverrides?.override_upscale_factor && !!integrationDefaults?.default_upscale_factor}
                />

                {/* Upscale Model */}
                {((hasOverrides && channelOverrides?.override_upscale_factor && channelOverrides.override_upscale_factor > 1.0) ||
                  (!hasOverrides && integrationDefaults?.default_upscale_factor && integrationDefaults.default_upscale_factor > 1.0)) && (
                  <SettingRow
                    label="KI-Modell"
                    value={
                      hasOverrides && channelOverrides?.override_upscale_model
                        ? MODEL_LABELS[channelOverrides.override_upscale_model] || channelOverrides.override_upscale_model
                        : integrationDefaults?.default_upscale_model
                        ? MODEL_LABELS[integrationDefaults.default_upscale_model] || integrationDefaults.default_upscale_model
                        : 'Nicht konfiguriert'
                    }
                    isDefault={hasOverrides && !channelOverrides?.override_upscale_model && !!integrationDefaults?.default_upscale_model}
                  />
                )}

                <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-4">
                  Optimierungs-Einstellungen werden bei der Integration konfiguriert.
                </p>
              </div>
            ) : (
              <div className="text-center py-12 text-neutral-500 dark:text-neutral-400">
                Keine Einstellungen konfiguriert
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <DialogFooter className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center justify-between w-full">
            <div>
              {activeTab === 'requirements' && requirements && (
                <Button
                  variant="outline"
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {deleteMutation.isPending ? t('common.deleting') : t('common.delete')}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={onClose}>
                {activeTab === 'optimization' ? t('common.close') : t('common.cancel')}
              </Button>
              {activeTab === 'requirements' && (
                <Button onClick={handleSave} disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? t('common.saving') : t('common.save')}
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Helper Components
// =============================================================================

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-50 mb-3">
        {title}
      </h3>
      {children}
    </div>
  );
}

function SettingRow({ label, value, isDefault }: { label: string; value: string; isDefault?: boolean }) {
  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
        {label}
      </label>
      <div className="text-base text-neutral-900 dark:text-neutral-50">
        {value}
        {isDefault && (
          <span className="ml-2 text-sm text-neutral-500 dark:text-neutral-400">
            (Standard)
          </span>
        )}
      </div>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900 dark:border-neutral-50" />
    </div>
  );
}
